import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { XCircle, RefreshCw, ArrowLeft, Loader2 } from 'lucide-react'
import { getScanResult } from '../services/api'
import ScanResults from '../components/ScanResults'
import LoadingAnimation from '../components/LoadingAnimation'


export default function ScanResultPage() {
  const { id } = useParams<{ id: string }>()
  const [showResults, setShowResults] = useState(false)

  const { data: scanResult, isLoading, isError } = useQuery({
    queryKey: ['scan', id],
    queryFn: () => getScanResult(id!),
    enabled: !!id && !showResults,
    refetchInterval: (data) => {
      if (data?.status === 'completed' || data?.status === 'failed') {
        setShowResults(true)
        return false
      }
      return 2000
    },
  })

  if (!id) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <p className="text-gray-600">No scan ID provided.</p>
        <Link to="/scan" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-700">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Start a new scan
        </Link>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <XCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-900">Scan not found</h3>
              <p className="text-sm text-red-700 mt-1">
                This scan result could not be loaded. It may have expired or the ID is invalid.
              </p>
              <Link
                to="/scan"
                className="mt-3 inline-flex items-center text-sm font-medium text-red-700 hover:text-red-800"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Start a new scan
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading || (!showResults && !scanResult)) {
    return (
      <div className="max-w-4xl mx-auto py-12 flex items-center justify-center gap-3 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading scan result...
      </div>
    )
  }

  if (!showResults && scanResult && scanResult.status !== 'completed' && scanResult.status !== 'failed') {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Accessibility Scan</h1>
          <p className="text-gray-600">Your scan is currently running...</p>
        </div>
        <LoadingAnimation url="" />
      </div>
    )
  }

  if (showResults && scanResult?.status === 'failed') {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <XCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-900">Scan Failed</h3>
              <p className="text-sm text-red-700 mt-1">
                The scan could not be completed. Please check the URL and try again.
              </p>
              <Link
                to="/scan"
                className="mt-3 inline-flex items-center text-sm font-medium text-red-700 hover:text-red-800"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Try Again
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <Link to="/scan" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" />
          New scan
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Accessibility Scan Results</h1>
      </div>
      <ScanResults scanId={id} />
    </div>
  )
}
